// Copyright (c) StrangeBee 2024

import { useEffect } from 'react';
import { QueryKey, useQuery, UseQueryResult } from 'react-query';
import { ParentController, QueryParams } from 'thehive-sdk';

export interface UseListQueryParams {
    queryParams: QueryParams;
    controller: ParentController;
    fetchOnStart?: boolean;
    manual?: boolean;
    retry?: boolean;
}

const useCustomQuery = <T>({
    queryParams,
    controller,
    fetchOnStart,
    manual = false,
    retry,
}: UseListQueryParams): UseQueryResult<T, QueryKey> => {
    const {
        selector: { name },
        paginationOptions,
        sort,
        filters,
    } = queryParams;

    const realManual = fetchOnStart || manual;

    const query = useQuery<T, QueryKey, any>(
        [name, paginationOptions?.page, paginationOptions?.itemsPerPage, filters, sort],
        () => controller.custom<T>(queryParams),
        {
            refetchInterval: false,
            refetchOnWindowFocus: false,
            enabled: !manual,
            retry,
        },
    );

    const { refetch, data, error } = query;

    useEffect(() => {
        if (error) {
            return;
        }
        const canRefresh = !realManual || (realManual && data === undefined);

        if (canRefresh) {
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetch, realManual]);

    return query;
};

export default useCustomQuery;
